/**
 * THE REFERRAL ECONOMICS — one constant, imported everywhere the number appears.
 *
 * Nick, 2026-08-31: a referred signup pays BOTH sides 500 — the invitee receives it as a
 * GIFT from the inviter (the ledger's actor is the inviter, so the record literally says
 * who gave it), and the inviter receives the same. The marketing leads with the gift:
 * "give a friend 500 points" is the benevolent half and the reason to share; "you get
 * 500 too" follows.
 *
 * Before this the amount was a magic literal `100` in four places (the award, the
 * Amplitude payload, and two i18n strings) and the invitee received nothing.
 */
/** Live / ungated signup award — develop behavior. Inviter only, no gift, no auto-connect. */
export const LIVE_REFERRAL_POINTS = 100;
/**
 * Default gated `referral_buddies` amount. Now the SEED/fallback only — the live
 * amount is admin-editable in `referral_settings.referralPoints`. Both sides, still
 * no auto-connect.
 */
export const REFERRAL_POINTS = 500;

/**
 * What a referred FRIEND receives under the live mechanism. The referrer always
 * earns the points amount regardless. Admin-switchable (`referral_settings`), one
 * live at a time. Shared so the API award, the model, and the invite copy agree.
 */
export type ReferralMechanism = 'points' | 'subscription' | 'points_and_subscription';
export const REFERRAL_MECHANISMS: readonly ReferralMechanism[] = ['points', 'subscription', 'points_and_subscription'];

/** Admin-selectable comped Human-Made Art gift terms, in months. */
export const GIFT_TERM_MONTHS = [1, 3, 6, 12] as const;
export type GiftTermMonths = (typeof GIFT_TERM_MONTHS)[number];

/** "1 month" / "3 months" / "1 year" — for invite copy and notifications. */
export function giftTermLabel(months: number): string {
  if (months === 12) {
    return '1 year';
  }
  if (months === 1) {
    return '1 month';
  }
  return `${months} months`;
}

/** Does the mechanism award the friend points? */
export function mechanismGrantsPoints(mechanism: ReferralMechanism): boolean {
  return mechanism === 'points' || mechanism === 'points_and_subscription';
}

/** Does the mechanism gift the friend a subscription? */
export function mechanismGrantsSubscription(mechanism: ReferralMechanism): boolean {
  return mechanism === 'subscription' || mechanism === 'points_and_subscription';
}
